import { ref, computed } from 'vue';
import enMessages from '../locales/en.json';
import deMessages from '../locales/de.json';
import ruMessages from '../locales/ru.json';
import { config } from '@/config';
export function useI18n() {
    const locale = ref('en');
    const API_BASE = config.api.baseUrl;
    const messages = {
        en: enMessages,
        de: deMessages,
        ru: ruMessages
    };
    const t = computed(() => messages[locale.value]);
    const changeLocale = (newLocale) => {
        locale.value = newLocale;
        localStorage.setItem('preferred-locale', newLocale);
    };
    const savedLocale = localStorage.getItem('preferred-locale');
    if (savedLocale && Object.keys(messages).includes(savedLocale)) {
        locale.value = savedLocale;
    }
    const blogArticlesFromFiles = ref([]);
    const camelToKebab = (str) => {
        return str
            .replace(/([a-z])([A-Z])/g, '$1-$2')
            .toLowerCase();
    };
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(locale.value, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };
    const getLocalizedEmailTexts = () => {
        const currentLocale = locale.value;
        const texts = {
            en: {
                subject: "Application for: ",
                greeting: "Hello!",
                body: "I would like to apply for the position \"",
                bodyEnd: "\".\n\nPlease find my CV attached.\n\nBest regards,",
                companyEmail: config.emails.vacancy
            },
            ru: {
                subject: "Отклик на вакансию: ",
                greeting: "Здравствуйте!",
                body: "Я хочу откликнуться на вакансию \"",
                bodyEnd: "\".\n\nМое резюме приложено.\n\nС уважением,",
                companyEmail: config.emails.vacancy
            },
            de: {
                subject: "Bewerbung für: ",
                greeting: "Hallo!",
                body: "Ich möchte mich für die Stelle \"",
                bodyEnd: "\" bewerben.\n\nBitte finden Sie meinen Lebenslauf im Anhang.\n\nMit freundlichen Grüßen,",
                companyEmail: config.emails.vacancy
            }
        };
        return texts[currentLocale] || texts.en;
    };
    const getLocalizedLevelNames = () => {
        const currentLocale = locale.value;
        const levels = {
            en: {
                junior: "Junior",
                middle: "Middle",
                senior: "Senior",
                lead: "Lead"
            },
            ru: {
                junior: "Начинающий специалист",
                middle: "Специалист",
                senior: "Старший специалист",
                lead: "Ведущий специалист"
            },
            de: {
                junior: "Junior",
                middle: "Mittelstufe",
                senior: "Senior",
                lead: "Leitung"
            }
        };
        return levels[currentLocale] || levels.en;
    };
    const apiGames = ref([]);
    const isLoadingGames = ref(false);
    const loadGamesFromAPI = async () => {
        try {
            isLoadingGames.value = true;
            const lang = locale.value;
            const gamesResponse = await fetch(`${API_BASE}/v1/games?lang=${lang}`);
            if (gamesResponse.ok) {
                const gamesData = await gamesResponse.json();
                if (gamesData.success) {
                    apiGames.value = gamesData.data.map((game) => ({
                        id: game.id,
                        title: game.title || 'Untitled Game',
                        subtitle: game.short_description || '',
                        description: game.description || '',
                        cover_image: game.cover_image,
                        release_date: game.release_date,
                        status: game.status,
                        is_featured: game.is_featured,
                        created_at: game.created_at,
                        short_description: game.short_description || ''
                    }));
                }
            }
            else {
                apiGames.value = [];
            }
        }
        catch (error) {
            apiGames.value = [];
        }
        finally {
            isLoadingGames.value = false;
        }
    };
    const apiVacancies = ref([]);
    const isLoadingVacancies = ref(false);
    const loadVacanciesFromAPI = async () => {
        try {
            isLoadingVacancies.value = true;
            const lang = locale.value;
            const response = await fetch(`${API_BASE}/v1/vacancies?lang=${lang}`);
            if (!response.ok) {
                apiVacancies.value = [];
                return;
            }
            const result = await response.json();
            if (result.success && result.data) {
                apiVacancies.value = result.data.map((vacancy) => {
                    let title = vacancy.title || 'Vacancy';
                    if (title === '[]' || title === '""' || title.trim() === '') {
                        title = 'Vacancy';
                    }
                    let excerpt = vacancy.excerpt || '';
                    if (excerpt === '[]' || excerpt === '""') {
                        excerpt = '';
                    }
                    const requirements = Array.isArray(vacancy.requirements)
                        ? vacancy.requirements
                        : (vacancy.requirements === '[]' || !vacancy.requirements ? [] : [String(vacancy.requirements)]);
                    const responsibilities = Array.isArray(vacancy.responsibilities)
                        ? vacancy.responsibilities
                        : (vacancy.responsibilities === '[]' || !vacancy.responsibilities ? [] : [String(vacancy.responsibilities)]);
                    const benefits = Array.isArray(vacancy.benefits)
                        ? vacancy.benefits
                        : (vacancy.benefits === '[]' || !vacancy.benefits ? [] : [String(vacancy.benefits)]);
                    let salaryRange = vacancy.salary_range || {};
                    if (typeof salaryRange === 'string') {
                        try {
                            salaryRange = JSON.parse(salaryRange);
                        }
                        catch {
                            salaryRange = {};
                        }
                    }
                    return {
                        id: vacancy.id,
                        title: title,
                        excerpt: excerpt,
                        category: vacancy.category || 'General',
                        level: vacancy.level || 'middle',
                        experienceRequired: vacancy.experience_required || 'Experience not specified',
                        employmentType: vacancy.employment_type || 'Full-time',
                        salaryRange: salaryRange,
                        isRemote: vacancy.is_remote || false,
                        isInternship: vacancy.is_internship || false,
                        isActive: vacancy.is_active !== false,
                        publishedAt: vacancy.published_at,
                        createdAt: vacancy.created_at,
                        requirements: requirements,
                        responsibilities: responsibilities,
                        benefits: benefits
                    };
                });
            }
            else {
                apiVacancies.value = [];
            }
        }
        catch (error) {
            apiVacancies.value = [];
        }
        finally {
            isLoadingVacancies.value = false;
        }
    };
    const loadGameById = async (id) => {
        try {
            const lang = locale.value;
            const response = await fetch(`${API_BASE}/v1/games/${id}?lang=${lang}`);
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    return data.data;
                }
            }
            return null;
        }
        catch (error) {
            return null;
        }
    };
    const loadGameMedia = async (gameId) => {
        try {
            const response = await fetch(`${API_BASE}/v1/games/${gameId}/media`);
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    return data.data;
                }
            }
            return [];
        }
        catch (error) {
            return [];
        }
    };
    const loadGameRequirements = async (gameId) => {
        try {
            const lang = locale.value;
            const response = await fetch(`${API_BASE}/v1/games/${gameId}/requirements?lang=${lang}`);
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    return data.data;
                }
            }
            return null;
        }
        catch (error) {
            return null;
        }
    };
    const loadBlogArticlesFromFiles = async () => {
        const response = await fetch('/blogs/index.json');
        if (!response.ok) {
            return;
        }
        const data = await response.json();
        const currentLang = locale.value;
        const articlesWithMeta = data.articles.map((article) => {
            const date = new Date(article.date);
            const formattedDate = date.toLocaleDateString(locale.value, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
            const category = article.category;
            const categoryTranslation = t.value.blog?.categories?.[category] || article.category;
            return {
                id: article.id,
                type: article.type || 'text',
                title: article.title?.[currentLang] || article.title?.en || article.id,
                excerpt: article.excerpt?.[currentLang] || article.excerpt?.en || '',
                category: article.category,
                categoryLabel: categoryTranslation,
                date: article.date,
                formattedDate: formattedDate,
                content: '',
                tags: article.tags || [],
                author: article.author,
                readTime: article.readTime,
                isFeatured: article.isFeatured || false,
                isPublished: article.isPublished !== false,
                video: article.video || null,
                image: article.image || null
            };
        });
        blogArticlesFromFiles.value = articlesWithMeta;
    };
    const loadBlogArticleFromFiles = async (articleId, lang = locale.value) => {
        try {
            const response = await fetch('/blogs/index.json');
            if (!response.ok) {
                throw new Error(`Failed to fetch index.json: ${response.status}`);
            }
            const data = await response.json();
            const article = data.articles.find((a) => a.id === articleId);
            if (!article) {
                throw new Error(`Article ${articleId} not found`);
            }
            const folderName = article.id;
            const contentUrl = `/blogs/${folderName}/${lang}.md`;
            const contentResponse = await fetch(contentUrl);
            let content = '';
            let selectedLang = lang;
            if (contentResponse.ok) {
                content = await contentResponse.text();
            }
            else {
                selectedLang = 'en';
                const fallbackUrl = `/blogs/${folderName}/en.md`;
                const fallbackResponse = await fetch(fallbackUrl);
                if (fallbackResponse.ok) {
                    content = await fallbackResponse.text();
                }
                else {
                    throw new Error(`Content file not found for ${folderName}`);
                }
            }
            const category = article.category;
            const date = new Date(article.date);
            const formattedDate = date.toLocaleDateString(locale.value, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
            return {
                id: article.id,
                title: article.title?.[selectedLang] || article.title?.en || '',
                excerpt: article.excerpt?.[selectedLang] || article.excerpt?.en || '',
                category: article.category,
                categoryLabel: t.value.blog?.categories?.[category] || article.category,
                date: article.date,
                formattedDate: formattedDate,
                content: content,
                rawContent: content,
                tags: article.tags || [],
                author: article.author,
                readTime: article.readTime,
                isFeatured: article.isFeatured || false,
                isPublished: article.isPublished !== false,
                language: selectedLang
            };
        }
        catch (error) {
            return null;
        }
    };
    const loadGameStores = async (gameId) => {
        try {
            const lang = locale.value;
            const response = await fetch(`${API_BASE}/v1/games/${gameId}/stores?lang=${lang}`);
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    return data.data;
                }
            }
            return [];
        }
        catch (error) {
            return [];
        }
    };
    const values = computed(() => {
        const items = t.value.values.items;
        return Object.entries(items).map(([id, item]) => ({
            id: id,
            title: item.title,
            description: item.description
        }));
    });
    const games = computed(() => {
        if (apiGames.value && apiGames.value.length > 0) {
            return apiGames.value;
        }
        const staticItems = t.value.games.items;
        return Object.entries(staticItems).map(([id, item]) => {
            const gameItem = item;
            return {
                id,
                title: gameItem.title,
                subtitle: gameItem.subtitle,
                description: gameItem.description
            };
        });
    });
    const apiNews = ref([]);
    const apiGameNews = ref([]);
    const isLoadingNews = ref(false);
    const loadNewsFromAPI = async () => {
        try {
            isLoadingNews.value = true;
            const lang = locale.value;
            const generalResponse = await fetch(`${API_BASE}/v1/news/general?lang=${lang}`);
            if (generalResponse.ok) {
                const generalData = await generalResponse.json();
                if (generalData.success) {
                    apiNews.value = generalData.data.map((news) => ({
                        id: news.id,
                        title: news.title,
                        excerpt: news.excerpt || '',
                        content: news.content || '',
                        date: news.published_at,
                        formattedDate: formatDate(news.published_at),
                        author: news.author || 'Desirion Studio',
                        news_image: news.news_image,
                        language: news.language
                    })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                }
            }
            const gameNewsResponse = await fetch(`${API_BASE}/v1/news/games?lang=${lang}`);
            if (gameNewsResponse.ok) {
                const gameNewsData = await gameNewsResponse.json();
                if (gameNewsData.success) {
                    apiGameNews.value = gameNewsData.data.map((news) => ({
                        id: news.id,
                        title: news.title,
                        excerpt: news.excerpt || '',
                        content: news.content || '',
                        date: news.published_at,
                        formattedDate: formatDate(news.published_at),
                        news_type: news.news_type || 'update',
                        version: news.version,
                        game_title: news.game_title,
                        game_id: news.game_id,
                        language: news.language
                    })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                }
            }
        }
        catch (error) {
            apiNews.value = [];
            apiGameNews.value = [];
        }
        finally {
            isLoadingNews.value = false;
        }
    };
    const allNews = computed(() => {
        const combined = [
            ...apiNews.value.map(news => ({
                ...news,
                type: 'general'
            })),
            ...apiGameNews.value.map(news => ({
                ...news,
                type: 'game'
            }))
        ];
        return combined.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    });
    const news = computed(() => {
        if (allNews.value.length > 0) {
            return allNews.value.map((item) => ({
                id: item.id,
                title: item.title,
                date: item.date,
                time: new Date(item.date).toLocaleTimeString(locale.value, {
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                excerpt: item.excerpt,
                content: item.content,
                formattedDate: formatDate(item.date)
            }));
        }
        const staticItems = t.value.news.items;
        return Object.entries(staticItems).map(([id, item]) => {
            const newsItem = item;
            return {
                id,
                title: newsItem.title,
                date: newsItem.date,
                time: newsItem.time,
                excerpt: newsItem.excerpt,
                content: newsItem.content,
                formattedDate: formatDate(newsItem.date)
            };
        });
    });
    const blog = computed(() => {
        const items = t.value.blog.items;
        const categories = t.value.blog.categories;
        return Object.entries(items).map(([id, item]) => ({
            id: id,
            title: item.title,
            category: item.category,
            categoryLabel: categories[item.category],
            date: item.date,
            excerpt: item.excerpt,
            content: item.content,
            formattedDate: formatDate(item.date)
        }));
    });
    const contactPlatforms = computed(() => {
        const platforms = t.value.contact.platforms;
        return Object.entries(platforms).map(([id, platform]) => ({
            id: id,
            name: platform.name,
            url: platform.url,
            icon: platform.icon
        }));
    });
    const apiVersions = ref({});
    const isLoadingVersions = ref(false);
    const loadGameVersions = async (gameId) => {
        try {
            isLoadingVersions.value = true;
            const lang = locale.value;
            const response = await fetch(`${API_BASE}/v1/games/${gameId}/versions`);
            if (!response.ok) {
                return [];
            }
            const result = await response.json();
            if (result.success && result.data) {
                apiVersions.value[gameId] = result.data;
                return result.data;
            }
            else {
                return [];
            }
        }
        catch (error) {
            return [];
        }
        finally {
            isLoadingVersions.value = false;
        }
    };
    return {
        t,
        currentLocale: locale,
        changeLocale,
        values,
        games,
        news,
        blog,
        blogArticlesFromFiles,
        loadBlogArticleFromFiles,
        loadBlogArticlesFromFiles,
        contactPlatforms,
        formatDate,
        apiNews,
        apiGameNews,
        allNews,
        isLoadingNews,
        loadNewsFromAPI,
        loadGamesFromAPI,
        loadGameById,
        loadGameMedia,
        loadGameRequirements,
        apiGames,
        isLoadingGames,
        isLoadingBlog: ref(false),
        getLocalizedEmailTexts,
        getLocalizedLevelNames,
        apiVersions,
        isLoadingVersions,
        loadGameVersions,
        loadGameStores,
        apiVacancies,
        isLoadingVacancies,
        loadVacanciesFromAPI,
        API_BASE
    };
}
